import React, { ReactElement } from "react";
//#region types
import { FormEnums } from "models/enums";
import { FormRowProps, FormControlLayoutProps, CustomFormFieldsProps } from "models/propTypes/genericFormProps";
import { FormGridLayout } from "models/propTypes/basePropTypes";
import { MessagePlacement } from "models/propTypes/messagePropTypes";
//#endregion
//#region components
import FormFieldGroup from "./formFields/formFieldGroup";
import FormFieldWrapper from "./formFieldWrapper";
import FormControlLayout from "./rowLayouts/formControlLayout";
import FormFieldSelector from "./formFieldSelector";
import { useFormikContext } from "formik";
import { FormHelper } from "utils/helpers";
//#endregion

function InlineFormControl<T extends Record<string, any>>(props: FormRowProps<T>) {
  const { name, field } = props;
  const { values } = useFormikContext<T>();

  const formGridLayout = (
    props.formGridLayout
      ? { ...props.formGridLayout }
      : {
          label: 3,
          message: undefined,
        }
  ) as FormGridLayout;

  formGridLayout.message =
    field.type === FormEnums.FormFieldType.TABLELIST ||
    field.type === FormEnums.FormFieldType.EDITABLETABLE ||
    field.type === FormEnums.FormFieldType.SUGGESTIONS
      ? false
      : formGridLayout.message;

  const getFormField = (messagePlacement: MessagePlacement, layout?: ReactElement<FormControlLayoutProps>) => {
    const hideOptionalIndicator = FormHelper.evaluateHideOptionalIndicator(field.hideOptionalIndicator, values);

    return field.type === FormEnums.FormFieldType.HORIONTALFIELDGROUP ? (
      <FormFieldGroup<T> {...props} messagePlacement={messagePlacement} layout={layout} />
    ) : field.type === FormEnums.FormFieldType.FORMFIELDSELECTOR ? (
      <FormFieldSelector<T> {...props} field={field} />
    ) : (
      <FormFieldWrapper<T>
        {...props}
        messagePlacement={messagePlacement}
        layout={layout}
        hideOptionalIndicator={hideOptionalIndicator}
      />
    );
  };

  const formControlLayoutProps: FormControlLayoutProps = {
    names: [name],
    label: field.label,
    controlLabel: field.controlLabel,
    descriptionText: field.descriptionText,
    inline: props.inline,
    verticalAlignLabelCenter: field.type === FormEnums.FormFieldType.TOGGLE,
    enableControlLables: props.enableControlLables,
    contentWidth: props.contentWidth,
    formGridLayout:
      field.type === FormEnums.FormFieldType.CUSTOM && field.extraProps
        ? { ...formGridLayout, message: (field.extraProps as CustomFormFieldsProps).formGridLayout?.message }
        : formGridLayout,
    className: field.classes?.formControl,
  };

  return field.type === FormEnums.FormFieldType.DIMENSIONSELECTOR ||
    field.type === FormEnums.FormFieldType.DIMENSIONINPUTSELECTOR ||
    field.type === FormEnums.FormFieldType.HORIONTALFIELDGROUP ||
    field.type === FormEnums.FormFieldType.FORMFIELDSELECTOR ||
    field.type === FormEnums.FormFieldType.SUBFORM ? (
    getFormField("right", <FormControlLayout {...formControlLayoutProps} />)
  ) : (
    <FormControlLayout {...formControlLayoutProps}>
      {field.type === FormEnums.FormFieldType.CUSTOM ? (
        <FormFieldWrapper
          {...props}
          messagePlacement={"right"}
          field={{
            ...props.field,
            extraProps: {
              formGridLayout: formGridLayout,
              hideInlineErrorText: true,
            },
          }}
          hideOptionalIndicator={FormHelper.evaluateHideOptionalIndicator(field.hideOptionalIndicator, values)}
        />
      ) : (
        <FormFieldWrapper
          {...props}
          messagePlacement={"right"}
          hideOptionalIndicator={FormHelper.evaluateHideOptionalIndicator(field.hideOptionalIndicator, values)}
        />
      )}
    </FormControlLayout>
  );
}

export default InlineFormControl;
